import React, { useState, useEffect } from 'react';
import './CookieConsent.css';
import { Link } from 'react-router-dom';
import { CONSENT_KEY } from '../../hooks/useCookieConsent';

/**
 * Non-essential localStorage keys — cleared immediately on "Reject".
 * Essential keys (auth-token, user-name, forge-cookie-consent) are never
 * cleared because they are required for the site to function correctly.
 */
const NON_ESSENTIAL_KEYS = ['ml-selections'];

const CookieConsent = () => {
  const [visible,   setVisible]   = useState(false);
  const [expanded,  setExpanded]  = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(CONSENT_KEY)) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const dispatch = () =>
    window.dispatchEvent(new Event('forge-consent-change'));

  const accept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    dispatch();
    setVisible(false);
  };

  const reject = () => {
    localStorage.setItem(CONSENT_KEY, 'rejected');
    NON_ESSENTIAL_KEYS.forEach(k => localStorage.removeItem(k));
    dispatch();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="cookie-overlay" role="dialog" aria-modal="true" aria-label="Cookie consent">
      <div className="cookie-banner">

        {/* Icon */}
        <div className="cookie-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"/>
            <path d="M8.5 8.5v.01"/><path d="M16 15.5v.01"/><path d="M12 12v.01"/>
          </svg>
        </div>

        {/* Text */}
        <div className="cookie-content">
          <p className="cookie-title">WE USE COOKIES</p>
          <p className="cookie-body">
            We use essential browser storage to keep you logged in and your cart active.
            Accepting also saves your product preferences (e.g. size selections).{' '}
            <button className="cookie-learn-more" onClick={() => setExpanded(e => !e)}>
              {expanded ? 'Show less ↑' : 'Learn more ↓'}
            </button>
          </p>

          {expanded && (
            <div className="cookie-detail">
              <div className="cookie-detail-col">
                <p className="cookie-detail-head cookie-detail-head--accept">✓ Essential (always active)</p>
                <ul>
                  <li><strong>auth-token</strong> — keeps you logged in (JWT, 7-day expiry)</li>
                  <li><strong>user-name</strong> — displays your name in the header</li>
                  <li><strong>forge-cookie-consent</strong> — remembers this choice</li>
                </ul>
              </div>
              <div className="cookie-detail-col">
                <p className="cookie-detail-head cookie-detail-head--reject">◎ Preference (optional)</p>
                <ul>
                  <li><strong>ml-selections</strong> — remembers your chosen product size (e.g. 100ml) across visits</li>
                </ul>
                <p style={{ fontSize: 11, color: '#444', marginTop: 8 }}>
                  Rejecting clears saved size preferences. Essential storage is unaffected.
                </p>
              </div>
              <p className="cookie-detail-note">
                We do <strong>not</strong> use advertising cookies or third-party tracking.
                No data is shared with advertisers.{' '}
                <Link to="/privacy-policy" className="cookie-policy-link" onClick={reject}>
                  Read our Privacy Policy →
                </Link>
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="cookie-actions">
          <button className="cookie-btn cookie-btn--accept" onClick={accept}>
            Accept All
          </button>
          <button className="cookie-btn cookie-btn--reject" onClick={reject}>
            Essential Only
          </button>
        </div>

      </div>
    </div>
  );
};

export default CookieConsent;
